import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { toQueryParams } from 'helpers/api';
import cookie from 'react-cookies';

const PORTAL_TOKEN_KEY = 'portal_token';

/**
 * Get portal token from storage
 */
export const getPortalToken = () => {
  return cookie.load(PORTAL_TOKEN_KEY);
};

/**
 * Set portal token in storage
 */
export const setPortalToken = token => {
  cookie.save(PORTAL_TOKEN_KEY, token, { path: '/' });
};

/**
 * Clear portal token from storage
 */
export const clearPortalToken = () => {
  cookie.remove(PORTAL_TOKEN_KEY, { path: '/' });
};

/**
 * Candidate Portal API
 *
 * Handles all candidate portal authentication and data endpoints.
 *
 * SECURITY MODEL:
 * - Uses separate token storage from main app (portal_token vs token)
 * - All protected endpoints require portal authentication
 * - Authorization is validated server-side via candidate_user_links
 */
export const candidatePortalApi = createApi({
  reducerPath: 'candidatePortalApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_API_URl}/candidate_portal`,
    prepareHeaders: (headers, { endpoint }) => {
      // Only add auth header for protected endpoints
      const publicEndpoints = ['decodeInvitation', 'requestOtp', 'verifyOtp', 'loginWithPassword'];

      if (!publicEndpoints.includes(endpoint)) {
        const token = getPortalToken();
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      }
      return headers;
    },
  }),
  tagTypes: [
    'PortalSession',
    'PortalDashboard',
    'PortalCheck',
    'PortalSubCheck',
    'PortalConsentFiles',
    'PortalUploads',
  ],
  endpoints: builder => ({
    // ==========================================
    // PUBLIC ENDPOINTS
    // ==========================================

    /**
     * Decode invitation token to get context
     */
    decodeInvitation: builder.query({
      query: token => `/invitation/${token}`,
      transformResponse: response => response.result,
    }),

    /**
     * Request OTP for login
     */
    requestOtp: builder.mutation({
      query: ({ email, candidateId, companyId }) => ({
        url: '/auth/otp/request',
        method: 'POST',
        body: {
          email,
          candidate_id: candidateId,
          company_id: companyId,
        },
      }),
      transformResponse: response => response.result,
    }),

    /**
     * Verify OTP and login
     */
    verifyOtp: builder.mutation({
      query: ({ email, otp, candidateId, companyId }) => ({
        url: '/auth/otp/verify',
        method: 'POST',
        body: {
          email,
          otp,
          candidate_id: candidateId,
          company_id: companyId,
        },
      }),
      transformResponse: (response, meta) => {
        // Priority: response body token (most reliable) > response header
        let token = response.result?.token;
        if (!token) {
          token = meta.response?.headers?.get('Authorization');
        }
        if (token) {
          token = token.replace(/^Bearer\s+/i, '');
          setPortalToken(token);
        }
        return response.result;
      },
      invalidatesTags: ['PortalSession'],
    }),

    /**
     * Login with password
     */
    loginWithPassword: builder.mutation({
      query: ({ email, password, candidateId, companyId }) => ({
        url: '/auth/password',
        method: 'POST',
        body: {
          email,
          password,
          candidate_id: candidateId,
          company_id: companyId,
        },
      }),
      transformResponse: (response, meta) => {
        // Priority: response body token (most reliable) > response header
        let token = response.result?.token;
        if (!token) {
          token = meta.response?.headers?.get('Authorization');
        }
        if (token) {
          token = token.replace(/^Bearer\s+/i, '');
          setPortalToken(token);
        }
        return response.result;
      },
      invalidatesTags: ['PortalSession'],
    }),

    // ==========================================
    // AUTHENTICATED ENDPOINTS
    // ==========================================

    /**
     * Get current session info
     */
    getSession: builder.query({
      query: () => '/auth/session',
      transformResponse: response => response.result,
      providesTags: ['PortalSession'],
    }),

    /**
     * Set password (after first OTP login)
     */
    setPassword: builder.mutation({
      query: password => ({
        url: '/auth/password/set',
        method: 'POST',
        body: { password },
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalSession'],
    }),

    /**
     * Select candidate context (when user has multiple)
     * Note: company_id is derived from the candidate on the backend
     */
    selectContext: builder.mutation({
      query: ({ candidateId }) => ({
        url: '/context/select',
        method: 'POST',
        body: {
          candidate_id: candidateId,
        },
      }),
      transformResponse: (response, meta) => {
        // Priority: response body token (most reliable) > response header
        let token = response.result?.token;
        if (!token) {
          token = meta.response?.headers?.get('Authorization');
        }
        if (token) {
          token = token.replace(/^Bearer\s+/i, '');
          setPortalToken(token);
        }
        return response.result;
      },
      invalidatesTags: ['PortalSession', 'PortalDashboard'],
    }),

    // ==========================================
    // PROTECTED ENDPOINTS (require context)
    // ==========================================

    /**
     * Get dashboard data
     */
    getDashboard: builder.query({
      query: () => '/dashboard',
      transformResponse: response => response.result,
      providesTags: ['PortalDashboard'],
    }),

    /**
     * Get check result file.
     * Returns a signed HTTPS URL when backend provides one (e.g. afp_backy), or a data URL
     * from the streamed file so we avoid blob: URLs and CSP issues.
     */
    getCheckResultFile: builder.query({
      queryFn: async ({ candidateCheckId }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/checks/${candidateCheckId}/result`,
          responseHandler: async response => {
            const contentType = response.headers.get('content-type');
            // Backend may return JSON with a signed URL (e.g. afp_backy) or stream the file
            if (contentType?.includes('application/json')) {
              const jsonResponse = await response.json();
              return jsonResponse.result;
            }
            // Streamed file: use data URL so we don't need blob: in CSP
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(blob);
            });
          },
        });

        if (response.error) {
          return { error: response.error };
        }

        return { data: response.data };
      },
      providesTags: (result, error, { candidateCheckId }) => [
        { type: 'PortalCheck', id: candidateCheckId },
      ],
    }),

    /**
     * Get sub check result file.
     * Returns a signed HTTPS URL when backend provides one, or a data URL from the stream.
     */
    getSubCheckResultFile: builder.query({
      queryFn: async ({ subCheckId }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/checks/sub/${subCheckId}/result`,
          responseHandler: async response => {
            const contentType = response.headers.get('content-type');
            if (contentType?.includes('application/json')) {
              const jsonResponse = await response.json();
              return jsonResponse.result;
            }
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(blob);
            });
          },
        });

        if (response.error) {
          return { error: response.error };
        }

        return { data: response.data };
      },
      providesTags: (result, error, { subCheckId }) => [{ type: 'PortalSubCheck', id: subCheckId }],
    }),

    /**
     * Get consent files
     */
    getConsentFiles: builder.query({
      query: () => '/consent/files',
      transformResponse: response => response.result,
      providesTags: ['PortalConsentFiles'],
    }),

    // ==========================================
    // UPLOAD ENDPOINTS
    // ==========================================

    /**
     * Sign upload (get presigned URL for S3)
     */
    signUpload: builder.query({
      query: params => `/uploads/sign?${toQueryParams(params)}`,
      transformResponse: response => response.result,
    }),

    /**
     * Get upload by ID
     */
    getUpload: builder.query({
      query: id => `/uploads/${id}`,
      transformResponse: response => response.result,
    }),

    /**
     * Create upload
     */
    createUpload: builder.mutation({
      query: params => ({
        url: '/uploads',
        method: 'POST',
        body: params,
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalUploads'],
    }),

    /**
     * Delete upload
     */
    deleteUpload: builder.mutation({
      query: id => ({
        url: `/uploads/${id}`,
        method: 'DELETE',
      }),
      transformResponse: response => response.result,
      invalidatesTags: ['PortalUploads'],
    }),

    /**
     * Update candidate check
     */
    updateCandidateCheck: builder.mutation({
      query: ({ candidateCheckId, params }) => ({
        url: `/checks/${candidateCheckId}`,
        method: 'PUT',
        body: params,
      }),
      transformResponse: response => response.result,
    }),

    /**
     * Submit check dispute
     */
    submitCheckDispute: builder.mutation({
      query: ({ candidateCheckId }) => ({
        url: `/checks/${candidateCheckId}/dispute`,
        method: 'POST',
      }),
      transformResponse: response => response.result,
    }),
  }),
});

export const {
  // Public endpoints
  useLazyDecodeInvitationQuery,
  useDecodeInvitationQuery,
  useRequestOtpMutation,
  useVerifyOtpMutation,
  useLoginWithPasswordMutation,
  // Authenticated endpoints
  useGetSessionQuery,
  useLazyGetSessionQuery,
  useSetPasswordMutation,
  useSelectContextMutation,
  // Protected endpoints
  useUpdateCandidateCheckMutation,
  useGetDashboardQuery,
  useLazyGetDashboardQuery,
  useGetCheckResultFileQuery,
  useLazyGetCheckResultFileQuery,
  useGetSubCheckResultFileQuery,
  useLazyGetSubCheckResultFileQuery,
  useSubmitCheckDisputeMutation,
  // Upload endpoints
  useLazySignUploadQuery,
  useGetUploadQuery,
  useLazyGetUploadQuery,
  useCreateUploadMutation,
  useDeleteUploadMutation,
  // Consent endpoints
  useGetConsentFilesQuery,
  useLazyGetConsentFilesQuery,
} = candidatePortalApi;
